import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import zipCodeData from "../constants/zipCodeData.json";
import { listCategories, listServices } from "../services/api";

const subcategoryKey = (category, type) => `${category}::${type}`;
const normalizeCostScale = (value) => Math.max(0, Math.min(5, Number(value || 0)));
const buildTelHref = (phone = "") => `tel:${String(phone).replace(/[^\d+]/g, "")}`;
const buildMapsHref = (address = "") => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(address || "").trim())}`;

const dedupeCategories = (items = []) => {
  const dedupeMap = new Map();
  items.forEach((item) => {
    const parentId = Number(item?.parentId || 0);
    const key = `${parentId}::${String(item?.slug || item?.name || "")
      .trim()
      .toLowerCase()}`;
    if (!dedupeMap.has(key)) {
      dedupeMap.set(key, item);
    }
  });
  return Array.from(dedupeMap.values());
};

function HelpFinderPanel() {
  const [searchText, setSearchText] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedSubcategoryKeys, setSelectedSubcategoryKeys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [zipCode, setZipCode] = useState("");
  const [useZipFilter, setUseZipFilter] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState({ type: "", message: "" });
  const [isMockMode, setIsMockMode] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      const response = await listCategories();
      setCategories(response.data || []);
      setIsMockMode(Boolean(response.mockMode));
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadServices = async () => {
      const response = await listServices({ search: searchText, categoryIds: [] });
      setServices(response.data || []);
      setIsMockMode((prev) => prev || Boolean(response.mockMode));
    };
    loadServices();
  }, [searchText]);

  const dedupedCategories = useMemo(() => dedupeCategories(categories), [categories]);
  const categoryLookup = useMemo(() => {
    const map = new Map();
    dedupedCategories.forEach((category) => map.set(Number(category.id), category.name));
    return map;
  }, [dedupedCategories]);
  const majorCategories = useMemo(() => dedupedCategories.filter((category) => !Number(category.parentId || 0)), [dedupedCategories]);
  const selectedCategoryNames = useMemo(() => selectedCategoryIds.map((id) => categoryLookup.get(Number(id))).filter(Boolean), [categoryLookup, selectedCategoryIds]);

  const zipLookup = useMemo(() => {
    const map = new Map();
    zipCodeData.forEach((item) => {
      if (!map.has(item.zipcode)) {
        map.set(item.zipcode, {
          lat: Number(item.latitude),
          lon: Number(item.longitude),
        });
      }
    });
    return map;
  }, []);

  const isZipValid = /^\d{5}$/.test(zipCode.trim());
  const userZipCoords = isZipValid ? zipLookup.get(zipCode.trim()) : null;
  const shouldApplyZip = useZipFilter && Boolean(userZipCoords) && !useCurrentLocation;
  const activeOriginCoords = useCurrentLocation ? currentLocation : shouldApplyZip ? userZipCoords : null;
  const shouldApplyDistance = Boolean(activeOriginCoords);

  const calculateDistanceMiles = (origin, target) => {
    const earthRadiusMiles = 3958.8;
    const toRadians = (value) => (value * Math.PI) / 180;
    const dLat = toRadians(target.lat - origin.lat);
    const dLon = toRadians(target.lon - origin.lon);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRadians(origin.lat)) * Math.cos(toRadians(target.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
  };

  const subcategoryOptions = useMemo(() => {
    const options = new Map();
    if (!selectedCategoryNames.length) return [];

    services.forEach((service) => {
      (service.subcategory || []).forEach((item) => {
        if (!item?.Category || !item?.Type) return;
        if (!selectedCategoryNames.includes(item.Category)) return;
        const key = subcategoryKey(item.Category, item.Type);
        if (!options.has(key)) {
          options.set(key, { key, category: item.Category, type: item.Type });
        }
      });
    });

    return Array.from(options.values()).sort((a, b) => `${a.category}${a.type}`.localeCompare(`${b.category}${b.type}`));
  }, [selectedCategoryNames, services]);

  const filteredServices = useMemo(
    () =>
      services
        .map((service) => {
          const serviceCategoryNames = Array.from(new Set(service.categoryNames || []));
          const serviceSubcategories = Array.from(new Map((service.subcategory || []).map((item) => [subcategoryKey(item?.Category, item?.Type), item])).values());

          const categoryMatch = selectedCategoryNames.length === 0 || serviceCategoryNames.some((name) => selectedCategoryNames.includes(name));
          const subcategoryMatch = selectedSubcategoryKeys.length === 0 || serviceSubcategories.some((item) => selectedSubcategoryKeys.includes(subcategoryKey(item.Category, item.Type)));

          if (!categoryMatch || !subcategoryMatch) return null;

          let distanceMiles = null;
          if (shouldApplyDistance) {
            const serviceCoords = zipLookup.get(service.zipcode || "");
            if (serviceCoords) {
              distanceMiles = calculateDistanceMiles(activeOriginCoords, serviceCoords);
              if (shouldApplyZip && Number(radiusMiles) > 0 && distanceMiles > Number(radiusMiles)) return null;
            } else if (shouldApplyZip) {
              return null;
            }
          }

          return {
            ...service,
            distanceMiles,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          if (shouldApplyDistance) return (a.distanceMiles ?? Number.MAX_SAFE_INTEGER) - (b.distanceMiles ?? Number.MAX_SAFE_INTEGER);
          return (a.title || "").localeCompare(b.title || "");
        }),
    [activeOriginCoords, radiusMiles, selectedCategoryNames, selectedSubcategoryKeys, services, shouldApplyDistance, shouldApplyZip, zipLookup],
  );

  useEffect(() => {
    if (!subcategoryOptions.length) {
      setSelectedSubcategoryKeys([]);
      return;
    }
    const validKeys = new Set(subcategoryOptions.map((option) => option.key));
    setSelectedSubcategoryKeys((prev) => prev.filter((key) => validKeys.has(key)));
  }, [subcategoryOptions]);

  const toggleCategory = (id) => {
    const numericId = Number(id);
    setSelectedCategoryIds((prev) => (prev.includes(numericId) ? prev.filter((item) => item !== numericId) : [...prev, numericId]));
  };

  const toggleSubcategory = (key) => {
    setSelectedSubcategoryKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const requestCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus({ type: "danger", message: "Location is not supported in this browser." });
      return;
    }
    setLocationStatus({ type: "info", message: "Requesting your location..." });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: Number(position.coords.latitude),
          lon: Number(position.coords.longitude),
        });
        setUseCurrentLocation(true);
        setUseZipFilter(false);
        setLocationStatus({ type: "success", message: "Using your current location for nearest results." });
      },
      () => {
        setLocationStatus({ type: "danger", message: "Location request denied or unavailable." });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  return (
    <div className="help-finder glass-card p-3 p-md-4 text-start">
      {isMockMode && (
        <Alert variant="warning" className="mb-3">
          Demo mode is active until API/SQL is deployed.
        </Alert>
      )}
      <div className="mb-2 fw-semibold">Search for veteran resources</div>
      <Form.Control
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search services, organizations, keywords..."
        className="mb-3"
      />

      <div className="mb-2 small text-muted-soft">Categories</div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {majorCategories.map((category) => {
          const isSelected = selectedCategoryIds.includes(Number(category.id));
          return (
            <Button
              key={`help-category-${category.id}`}
              size="sm"
              type="button"
              variant={isSelected ? "warning" : "outline-info"}
              className="category-filter-chip"
              onClick={() => toggleCategory(category.id)}
            >
              {category.name}
            </Button>
          );
        })}
      </div>

      {subcategoryOptions.length > 0 && (
        <>
          <div className="mb-2 small text-muted-soft">Subcategories</div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {subcategoryOptions.map((item) => {
              const selected = selectedSubcategoryKeys.includes(item.key);
              return (
                <Button
                  key={`help-subcategory-${item.key}`}
                  size="sm"
                  type="button"
                  variant={selected ? "success" : "outline-secondary"}
                  className="category-filter-chip"
                  onClick={() => toggleSubcategory(item.key)}
                >
                  {item.category} {"->"} {item.type}
                </Button>
              );
            })}
          </div>
        </>
      )}

      <Row className="g-2 align-items-end mb-3">
        <Col sm={4}>
          <Form.Group>
            <Form.Label className="small mb-1">ZIP Code</Form.Label>
            <Form.Control
              value={zipCode}
              maxLength={5}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="e.g. 55066"
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label className="small mb-1">Radius (miles)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={500}
              value={radiusMiles}
              onChange={(e) => setRadiusMiles(Number(e.target.value || 25))}
            />
          </Form.Group>
        </Col>
        <Col sm={4} className="d-flex flex-column flex-sm-row gap-2">
          <Button
            type="button"
            size="sm"
            variant={useZipFilter ? "warning" : "outline-warning"}
            onClick={() => {
              setUseZipFilter((prev) => !prev);
              setUseCurrentLocation(false);
            }}
            className="w-100"
          >
            {useZipFilter ? "ZIP Filter On" : "Use ZIP Filter"}
          </Button>
          <Button type="button" size="sm" variant="outline-info" onClick={requestCurrentLocation} className="w-100">
            Use My Location
          </Button>
        </Col>
      </Row>

      {useZipFilter && zipCode.trim() && !isZipValid && (
        <Alert variant="warning" className="py-2">
          Enter a valid 5-digit ZIP code to apply ZIP radius filtering.
        </Alert>
      )}
      {locationStatus.message && (
        <Alert variant={locationStatus.type || "secondary"} className="py-2">
          {locationStatus.message}
        </Alert>
      )}

      <div className="help-results-scroll d-flex flex-column gap-3">
        {filteredServices.map((service) => (
          <Card key={`help-result-${service.id}`} className="resource-card text-light">
            <Card.Body>
              <Card.Title className="mb-1">{service.title || service.name || "Resource"}</Card.Title>
              {service.distanceMiles != null && (
                <div className="small text-info mb-2">{service.distanceMiles.toFixed(1)} miles away</div>
              )}
              <p className="small text-muted-soft mb-2">{service.description || "No description provided."}</p>

              <div className="d-flex flex-wrap gap-2 mb-2">
                {(service.categoryNames || [])
                  .filter(Boolean)
                  .filter((name, index, arr) => arr.indexOf(name) === index)
                  .map((name) => (
                    <Badge key={`${service.id}-${name}`} bg="secondary">
                      {name}
                    </Badge>
                  ))}
              </div>

              <div className="cost-scale-row mb-2">
                {Array.from({ length: 5 }).map((_, index) => {
                  const active = index < normalizeCostScale(service.costScale);
                  return (
                    <span key={`${service.id}-cost-${index}`} className={`cost-dollar ${active ? "is-active" : ""}`}>
                      $
                    </span>
                  );
                })}
              </div>

              <div className="small d-flex flex-column gap-1">
                {service.phone && (
                  <div>
                    <strong>Phone:</strong> <a href={buildTelHref(service.phone)}>{service.phone}</a>
                  </div>
                )}
                {service.website && (
                  <div>
                    <strong>Website:</strong>{" "}
                    <a href={service.website} target="_blank" rel="noreferrer">
                      {service.website}
                    </a>
                  </div>
                )}
                {service.address && (
                  <div>
                    <strong>Address:</strong> {service.address}{" "}
                    <a href={buildMapsHref(service.address)} target="_blank" rel="noreferrer">
                      Map
                    </a>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
        {filteredServices.length === 0 && (
          <Card className="resource-card text-light">
            <Card.Body className="small text-muted-soft">No matches found for your current filters. Adjust filters and try again.</Card.Body>
          </Card>
        )}
      </div>
    </div>
  );
}

export default HelpFinderPanel;
